"use client"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"
import QRCode from "react-qr-code"
import { twMerge } from "tailwind-merge"

interface GetAppButtonsProps {
  justifyContent?: string
  size?: "regular" | "small"
  className?: string
  forceButtons?: boolean
}

export default function GetAppButtons({
  justifyContent = "justify-center",
  size = "regular",
  className,
  forceButtons,
}: GetAppButtonsProps) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null)

  useEffect(() => {
    if (typeof navigator !== "undefined") {
      const mobile = /iPhone|iPad|iPod|Android/i.test(
        navigator.userAgent || (navigator as any).vendor || (window as any).opera,
      )
      setIsMobile(mobile)
    }
  }, [])

  if (forceButtons) {
    return (
      <div className="flex flex-col items-center gap-4">
        <DownloadQrCode size={size} className="my-0 mb-8" />
        <AppButtons justifyContent={justifyContent} className="mt-0" />
      </div>
    )
  }

  if (isMobile) {
    return <AppButtons justifyContent={justifyContent} className={className} />
  }

  return <DownloadQrCode size={size} className={className} />
}

export const AppButtons: React.FC<GetAppButtonsProps> = ({ justifyContent = "justify-center", className }) => (
  <div className={twMerge(`flex flex-wrap ${justifyContent} gap-4 mt-8`, className)}>
    <Link
      id="download-app-apple"
      href="https://apps.apple.com/nl/app/goodshift/id1573545734"
      target="_blank"
      rel="noopener noreferrer">
      <Image
        className="w-full h-full"
        src="/goodshift/img/banners/apple_badge.svg"
        width={300}
        height={101}
        priority
        alt="apple store badge"
      />
    </Link>
    <Link
      id="download-app-android"
      href="https://play.google.com/store/apps/details?id=foundation.goodplace.app"
      target="_blank"
      rel="noopener noreferrer">
      <Image
        className="w-full h-full"
        src="/goodshift/img/banners/google_badge.svg"
        width={336}
        height={101}
        priority
        alt="google store badge"
      />
    </Link>
  </div>
)

export const DownloadQrCode: React.FC<GetAppButtonsProps> = ({ size, className }) => {
  const qrCodeValue = `${process.env.NEXT_PUBLIC_SITE_URL ?? "https://goodshift.nl"}/download`
  const codeSize = size === "regular" ? 256 : 100
  const codeStyles =
    size === "regular" ? `max-w-[${codeSize}px] p-4 rounded-xl` : `max-w-[${codeSize}px] p-2 rounded-lg`

  return (
    <div className={twMerge("flex flex-wrap justify-center gap-4 my-8", className)}>
      <div className={twMerge("h-auto  bg-white", codeStyles)}>
        <QRCode
          size={codeSize}
          className="h-auto max-w-full w-full"
          value={qrCodeValue}
          viewBox={`0 0 ${codeSize} ${codeSize}`}
        />
      </div>
    </div>
  )
}
